import { useEffect, useMemo, useRef } from '@wordpress/element';

import ImagePreview from './ImagePreview';
import './styles.scss';
import getDefaultCrop from './utils/getDefaultCrop';

/**
 * Image variations component, used to display and edit a set of image variations.
 *
 * This renders a set of image previews based on a configuration object and source image data.
 *
 * It allows the user to adjust the crop of each image, or replace it with a new image, returning a
 * 'variations' object which contains the image IDs and crop data for each variation.
 *
 * @param {Object}                                                       props             Component props
 * @param {VariationsConfig}                                             props.config      The configuration for the image variations
 * @param {Variations}                                                   props.variations  The current variations
 * @param {Function}                                                     props.onChange    The function to update the variations
 * @param {Function}                                                     props.onInit      The function to initialise the variations
 * @param {{id: string, media_details: {width: number, height: number}}} props.sourceImage The source image data
 *
 * @return {JSX.Element}
 */
const ImageVariationPicker = ({ config, variations = {}, onChange, onInit, sourceImage }) => {
  const isInitialized = useRef(false);

  /**
   * Updates a variation value, replacing the entire object.
   *
   * @param {string}        key      The key of the variation to update
   * @param {VariationItem} newImage The new value to set
   */
  const replaceImage = (key, newImage) => {
    const defaultCrop = getDefaultCrop(newImage.width, newImage.height, config[key].aspectRatio);

    const newVariations = {
      ...variations,
      [key]: {
        id: newImage.id,
        crop: defaultCrop,
      },
    };

    onChange(newVariations);
  };

  /**
   * Updates a variation crop value without overwriting the rest of the object.
   *
   * @param {string} key  The key of the variation to update
   * @param {Crop}   crop The new crop value
   */
  const updateCrop = (key, crop) => {
    const existingValue = variations[key] || {};
    const newVariations = {
      ...variations,
      [key]: {
        ...existingValue,
        crop,
      },
    };

    onChange(newVariations);
  };

  /**
   * Removes a variation image, resetting it to the source image.
   *
   * @param {string} key The key of the variation to remove
   */
  const removeImage = (key) => {
    const existingValue = variations[key];

    if (!existingValue) {
      return;
    }

    const defaultCrop = getDefaultCrop(
      sourceImage.media_details.width,
      sourceImage.media_details.height,
      config[key].aspectRatio,
    );

    const newVariations = {
      ...variations,
      [key]: {
        crop: defaultCrop,
      },
    };

    onChange(newVariations);
  };

  /**
   * Generate initial variations based on the config.
   *
   * If a variation is not set, a default crop of the source image will be added.
   */
  const initialVariations = useMemo(() => {
    if (!sourceImage) {
      return {};
    }
    return Object.entries(config).reduce((object, [key, value]) => {
      if (variations?.[key]) {
        return {
          ...object,
          [key]: variations[key],
        };
      }

      const defaultCrop = getDefaultCrop(
        sourceImage.media_details.width,
        sourceImage.media_details.height,
        value.aspectRatio,
      );

      return {
        ...object,
        [key]: {
          crop: defaultCrop,
        },
      };
    }, {});
  }, [config, variations, sourceImage]);

  useEffect(() => {
    if (sourceImage && !isInitialized.current) {
      onInit(initialVariations);
      isInitialized.current = true;
    }
  }, [initialVariations, sourceImage, onInit]);

  return (
    <div className="mediapress-image-variation-picker">
      <div className="mediapress-image-variation-picker__thumbnails">
        {Object.entries(variations).map(([key, variation]) => {
          const configItem = config[key];
          const isOriginal = !variation.id || variation.id === sourceImage.id;
          const imageId = variation.id || sourceImage.id;

          return (
            <div className="mediapress-image-variation-picker__thumbnails__item" key={key}>
              <ImagePreview
                imageId={imageId}
                isOriginal={isOriginal}
                aspectRatio={configItem.aspectRatio}
                aspectRatioLabel={configItem.aspectRatioLabel}
                label={configItem.label}
                isCircular={configItem.style === 'round'}
                crop={variation.crop}
                onAdjustCrop={(crop) => {
                  updateCrop(key, crop);
                }}
                onImageChange={(id, width, height) => {
                  replaceImage(key, { id, width, height });
                }}
                onImageRemove={() => removeImage(key)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ImageVariationPicker;
